import React, { useState, useEffect, useCallback } from 'react';

const sectionStyle = { background: 'var(--theme-bg-light)', border: '1px solid var(--theme-border)', borderRadius: 8, padding: '1.2rem 1.4rem', marginTop: '1.2rem' };

function PmsUpsellEngine({ token, selectedProperty }) {
  const [suggestions, setSuggestions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const headers = useCallback((withJson = false) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    ...(withJson ? { 'Content-Type': 'application/json' } : {}),
  }), [token, selectedProperty]);

  const loadSuggestions = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true);
    try {
      const res = await fetch('/api/v1/ai/upsell-engine/suggestions', { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load');
      setSuggestions(data.suggestions || []);
      setSummary(data.summary || null);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [selectedProperty?.id, headers]);

  useEffect(() => { loadSuggestions(); }, [loadSuggestions]);

  const generate = async () => {
    setGenerating(true); setError(''); setMessage('');
    try {
      const res = await fetch('/api/v1/ai/upsell-engine/generate', { method: 'POST', headers: headers(true), body: '{}' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setMessage(`Generated ${data.suggestion_count} upsell opportunities`);
      setTimeout(() => setMessage(''), 4000);
      await loadSuggestions();
    } catch (err) { setError(err.message); }
    finally { setGenerating(false); }
  };

  const handleAction = async (id, action, revenue) => {
    try {
      await fetch(`/api/v1/ai/upsell-engine/suggestions/${id}/action`, {
        method: 'POST', headers: headers(true),
        body: JSON.stringify({ action, outcome_revenue: revenue }),
      });
      setSuggestions(prev => prev.map(s => s.id === id ? { ...s, status: action } : s));
    } catch (_) { /* ignore */ }
  };

  if (!selectedProperty?.id) {
    return (<div style={{ padding: '1rem', maxWidth: 1000 }}><h2 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Upsell & Cross-Sell Engine</h2><p style={{ color: 'var(--theme-text-muted)' }}>Please select a property.</p></div>);
  }

  const formatCurrency = (v) => v != null ? `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-';
  const triggerColors = { CHECKIN: '#3b82f6', DURING_STAY: '#8b5cf6', PRE_DEPARTURE: '#f59e0b', LOW_OCCUPANCY: '#ef4444' };
  const typeLabels = { ROOM_UPGRADE: 'Room Upgrade', FNB_PROMO: 'F&B', LATE_CHECKOUT: 'Late Checkout', AMENITY: 'Amenity', EXPERIENCE: 'Experience' };

  return (
    <div style={{ padding: '1rem', maxWidth: 1100 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div>
          <h2 style={{ marginTop: 0, color: 'var(--theme-primary)', marginBottom: 4 }}>Upsell & Cross-Sell Engine</h2>
          <p style={{ color: 'var(--theme-text-secondary)', fontSize: '0.85rem', margin: 0 }}>AI-identified revenue opportunities for in-house guests</p>
        </div>
        <button onClick={generate} disabled={generating} style={{
          padding: '0.5rem 1.2rem', borderRadius: 6, border: 'none', background: generating ? 'var(--theme-text-muted)' : '#10b981',
          color: '#fff', fontWeight: 600, cursor: generating ? 'wait' : 'pointer', fontSize: '0.85rem',
        }}>{generating ? 'Scanning...' : 'Scan Guests'}</button>
      </div>

      {error && <div className="error-banner" style={{ marginTop: '0.5rem' }}>{error}</div>}
      {message && <div className="success-banner" style={{ marginTop: '0.5rem' }}>{message}</div>}

      {loading ? (
        <div style={{ color: 'var(--theme-text-muted)', padding: '2rem', textAlign: 'center' }}>Loading...</div>
      ) : suggestions.length === 0 ? (
        <div style={sectionStyle}><div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--theme-text-muted)' }}>
          <div style={{ fontSize: '1.3rem', marginBottom: '0.5rem' }}>No upsell opportunities</div>
          <div style={{ fontSize: '0.85rem' }}>Click "Scan Guests" to analyze in-house guests for revenue opportunities.</div>
        </div></div>
      ) : (
        <>
          {summary && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginTop: '1rem' }}>
              <SCard label="Opportunities" value={summary.total} color="var(--theme-primary)" />
              <SCard label="Potential Revenue" value={formatCurrency(summary.potential_revenue)} color="#10b981" />
              {Object.entries(summary.by_trigger || {}).map(([k, v]) => (
                <SCard key={k} label={k.replace(/_/g, ' ')} value={v} color={triggerColors[k] || 'var(--theme-text-secondary)'} />
              ))}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1rem' }}>
            {suggestions.filter(s => s.status !== 'DECLINED').map(s => (
              <div key={s.id} style={{ background: '#fff', border: '1px solid var(--theme-border)', borderRadius: 10, padding: '1rem 1.2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 600, background: triggerColors[s.trigger_type] || 'var(--theme-border)', color: '#fff' }}>{s.trigger_type?.replace(/_/g, ' ')}</span>
                      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 600, background: 'var(--theme-border-light)', color: '#475569' }}>{typeLabels[s.suggestion_type] || s.suggestion_type}</span>
                      {s.confidence === 'HIGH' && <span style={{ padding: '2px 6px', borderRadius: 3, fontSize: '0.65rem', background: '#dcfce7', color: 'var(--theme-success)' }}>HIGH</span>}
                    </div>
                    <div style={{ fontWeight: 700, fontSize: '0.92rem', color: '#1e293b' }}>Room {s.room_number} — {s.guest_name}</div>
                    <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#334155', marginTop: 2 }}>{s.suggestion_title}</div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--theme-text-secondary)', marginTop: 4 }}>{s.suggestion_detail}</div>
                    {s.guest_acceptance_history && <div style={{ fontSize: '0.75rem', color: '#8b5cf6', marginTop: 4 }}>{s.guest_acceptance_history}</div>}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#10b981' }}>{formatCurrency(s.potential_revenue)}</div>
                    {s.status === 'ACTIVE' && (
                      <div style={{ display: 'flex', gap: '0.3rem', marginTop: 6 }}>
                        <button onClick={() => handleAction(s.id, 'ACCEPTED', s.potential_revenue)} style={{ padding: '0.3rem 0.7rem', borderRadius: 4, border: 'none', background: 'var(--theme-success)', color: '#fff', cursor: 'pointer', fontSize: '0.78rem' }}>Accept</button>
                        <button onClick={() => handleAction(s.id, 'DECLINED')} style={{ padding: '0.3rem 0.7rem', borderRadius: 4, border: '1px solid var(--theme-border)', background: '#fff', color: 'var(--theme-text-secondary)', cursor: 'pointer', fontSize: '0.78rem' }}>Decline</button>
                      </div>
                    )}
                    {s.status === 'ACCEPTED' && <span style={{ fontSize: '0.78rem', color: 'var(--theme-success)', fontWeight: 600 }}>Accepted</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SCard({ label, value, color }) {
  return (<div style={{ background: '#fff', border: '1px solid var(--theme-border)', borderRadius: 8, padding: '0.75rem 1rem', textAlign: 'center' }}>
    <div style={{ fontSize: '0.75rem', color: 'var(--theme-text-secondary)', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: '1.3rem', fontWeight: 700, color }}>{value}</div>
  </div>);
}

export default PmsUpsellEngine;
